import { useState, useCallback } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import WordPressCard from './WordPressCard';
import ShopifyCard from './ShopifyCard';
import SupabaseCard from './SupabaseCard';
import { useLanguage } from '../context/LanguageContext';
import lovableIcon from '../assets/lovable.svg';
import wordpressIcon from '../assets/wordpress.svg';

// Brand glyphs shown next to each sub-item (visual only).
const LovableGlyph = () => (
  <img src={lovableIcon} alt="" className="w-5 h-5 rounded-[5px] shrink-0" />
);
const WordPressGlyph = () => (
  <img src={wordpressIcon} alt="" className="w-5 h-5 rounded-[5px] shrink-0" />
);

const ShopifyGlyph = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 rounded-[5px] shrink-0" aria-hidden="true">
    <rect width="24" height="24" fill="#95BF47" />
    <path fill="#fff" d="M15.3 5.6c0-.1-.1-.2-.2-.2l-1.5-.1-1-1c-.1-.1-.3 0-.4 0l-.5.1c-.3-.9-.9-1.8-1.9-1.8h-.1c-.3-.4-.6-.5-.9-.5-2.2 0-3.2 2.8-3.6 4.2l-1.5.5c-.5.1-.5.1-.5.6L3.9 18l9.4 1.8 5.1-1.1-3.1-13.1zm-3.8.9l-1.9.6c.2-.7.6-1.5 1.2-1.7.3-.1.5-.2.7-.2v1.3zm-1.1-2.3c.1 0 .3.1.4.1-.8.4-1.300 1.300-1.600 2.400l-1.600.5c.3-1.300 1.200-3 2.800-3z"/>
  </svg>
);

const STRINGS = {
  de: {
    title: 'Website',
    subtitle: 'Wohin deine generierten Blog-Artikel veröffentlicht werden.',
    lovable: 'Lovable-Website',
    wordpress: 'WordPress',
    shopify: 'Shopify',
    connected: 'Verbunden',
    notConnected: 'Nicht verbunden',
  },
  en: {
    title: 'Website',
    subtitle: 'Where your generated blog articles get published.',
    lovable: 'Lovable-Website',
    wordpress: 'WordPress',
    shopify: 'Shopify',
    connected: 'Connected',
    notConnected: 'Not connected',
  },
};

/**
 * WebsiteIntegrationCard — umbrella "Website" card grouping the concrete publish
 * targets (Lovable-Website / WordPress / Shopify) as expandable sections.
 *
 * Status handling (fixes the "expand flips connected → not connected" bug):
 *  - The embedded cards are the SINGLE source of status. They are ALWAYS mounted
 *    (hidden via CSS when collapsed), so each loads its status exactly ONCE on
 *    mount and reports it via onStatusChange. Expanding/collapsing is a pure CSS
 *    toggle — it never mounts/unmounts a card and never re-fetches.
 *  - The umbrella itself does NOT fetch, so there is no second query to race with
 *    or overwrite the card's status.
 */
export default function WebsiteIntegrationCard({ getAuthHeader }) {
  const { lang } = useLanguage();
  const t = STRINGS[lang === 'en' ? 'en' : 'de'];
  // null = unknown/loading, true/false once the embedded card reports.
  const [statuses, setStatuses] = useState({ lovable: null, wordpress: null, shopify: null });
  const [open, setOpen] = useState({ lovable: false, wordpress: false, shopify: false });

  const onLovableStatus = useCallback((connected) => setStatuses((s) => ({ ...s, lovable: connected })), []);
  const onWordpressStatus = useCallback((connected) => setStatuses((s) => ({ ...s, wordpress: connected })), []);

  const onShopifyStatus = useCallback((connected) => setStatuses((s) => ({ ...s, shopify: connected })), []);

  const statusBadge = (connected) => {
    if (connected === null || connected === undefined) {
      return <span className="text-[10px] text-[#5A6C7D]">…</span>;
    }
    return connected ? (
      <span className="text-[10px] flex items-center gap-1 text-emerald-400">
        <Check className="w-3 h-3" /> {t.connected}
      </span>
    ) : (
      <span className="text-[10px] text-[#5A6C7D]">{t.notConnected}</span>
    );
  };

  // Plain render helper (NOT a nested component). The card is ALWAYS rendered;
  // collapsing just hides it (display:none) so it stays mounted → loads once.
  const renderSection = (id, name, icon, isLast, children) => (
    <div key={id} className={isLast ? '' : 'border-b border-[#0A1628]'} data-testid={`website-item-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => ({ ...o, [id]: !o[id] }))}
        aria-expanded={open[id]}
        className="w-full flex items-center gap-3 py-3 text-left"
        data-testid={`website-toggle-${id}`}
      >
        {icon}
        <span className="text-white font-medium">{name}</span>
        <span className="ml-auto" data-testid={`website-status-${id}`}>{statusBadge(statuses[id])}</span>
        <ChevronDown
          className={`w-4 h-4 text-[#5A6C7D] shrink-0 transition-transform duration-200 ${open[id] ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={open[id] ? 'pb-3' : 'hidden'}>{children}</div>
    </div>
  );

  return (
    <div className="bg-[#1A2B42] rounded-lg p-5" data-testid="website-integration-card">
      {/* Umbrella header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0A1628]">
          <Globe className="w-6 h-6 text-[#00D9C8]" aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-white font-medium">{t.title}</h3>
          <p className="text-xs text-[#5A6C7D]">{t.subtitle}</p>
        </div>
      </div>

      {renderSection('lovable', t.lovable, <LovableGlyph />, false, (
        <SupabaseCard getAuthHeader={getAuthHeader} embedded onStatusChange={onLovableStatus} />
      ))}
      {renderSection('wordpress', t.wordpress, <WordPressGlyph />, false, (
        <WordPressCard getAuthHeader={getAuthHeader} embedded onStatusChange={onWordpressStatus} />
      ))}
      {renderSection('shopify', t.shopify, <ShopifyGlyph />, true, (
        <ShopifyCard getAuthHeader={getAuthHeader} embedded onStatusChange={onShopifyStatus} />
      ))}
    </div>
  );
}
